import { Stack, Typography } from '@mui/material';

import Note from '@/designs-dev/annotations/Note';
import { type HandoverSpec } from '@/designs-dev/handover/types';
import Flow from '@/karehero-app/tell-us-once/intake/Flow';

// A row's last step that is not a screen of its own: where it rejoins the Happy
// path, said on a screen-sized card so it reads as part of the row.
const restAsNormal = (from: string) => () => (
  <Stack
    spacing={1}
    sx={{
      height: '100%',
      p: 3,
      alignItems: 'center',
      justifyContent: 'center',
      textAlign: 'center',
      bgcolor: 'background.default',
    }}
  >
    <Typography variant='h4'>The rest of the flow, as normal</Typography>
    <Typography variant='body2' sx={{ color: 'text.secondary' }}>
      {`From ${from} on, the same as the Happy path.`}
    </Typography>
  </Stack>
);

// The same screens as built on dev (demo/onboarding-2-0-live-facts).
const onDev = (initial: Parameters<typeof Flow>[0]['initial']) => () => (
  <Flow initial={initial} option={2} version='2.0-dev' />
);

// Dev, off the happy path: the summary mishears forty hours as fourteen and
// they type the correction.
const onDevCorrected =
  (initial: Parameters<typeof Flow>[0]['initial']) => () => (
    <Flow initial={initial} option={2} version='2.0-dev' branch='corrected' />
  );

// Dev, caring for themselves rather than someone else.
const onDevSelf = (initial: Parameters<typeof Flow>[0]['initial']) => () => (
  <Flow initial={initial} option={2} version='2.0-dev' branch='self' />
);

// Caring for themselves, through the parked middle of the journey.
const onDevSelfFull =
  (initial: Parameters<typeof Flow>[0]['initial']) => () => (
    <Flow initial={initial} option={2} version='2.0-dev' branch='self-full' />
  );

// Dev, off the happy path: their first answer leaves out what they need help
// with.
const onDevMissing = (initial: Parameters<typeof Flow>[0]['initial']) => () => (
  <Flow initial={initial} option={2} version='2.0-dev' branch='missing' />
);

/**
 * Onboarding 2.0, as far as it goes. Only the new first question is designed;
 * the rest of the flow is 1.1's and lives on that page.
 */
export const onboarding20Spec: HandoverSpec = {
  id: 'onboarding-2-0',
  title: 'Onboarding 4.0',
  description:
    'Onboarding as one conversation, from what they need help with to what they could get.',
  canvas: true,
  links: [
    { label: 'Onboarding 1.1', url: '/tell-us-once/flows/onboarding-1-1' },
  ],
  flows: [
    {
      title: 'Happy path',
      arrows: false,
      steps: [
        {
          id: 'dev-consent',
          title: 'What we do, and the consent',
          render: onDev('chat-v2'),
        },
        // Off while You're talking to Keni is hidden: it is that row tapped.
        // {
        //   id: 'dev-keni-about',
        //   title: 'Keni the llama',
        //   render: onDev('keni-about'),
        // },
        {
          id: 'dev-consent-explain',
          title: 'How we use AI',
          render: onDev('consent-explain'),
        },
        {
          id: 'dev-help',
          title: 'The open question',
          render: onDev('help-ask'),
        },
        {
          id: 'dev-recording',
          title: 'Recording',
          render: onDev('help-recording'),
        },
        {
          id: 'dev-recording-captions',
          title: 'Recording, captions on',
          render: onDev('help-recording-captions'),
        },
        {
          id: 'dev-help-processing',
          title: 'Reading what they said',
          render: onDev('help-processing'),
        },
        {
          id: 'dev-summary',
          title: 'Does this look right?',
          render: onDev('help-summary'),
        },
        {
          id: 'dev-questions',
          title: 'What the summary left',
          render: onDev('help-questions'),
        },
        {
          id: 'dev-age',
          title: 'How old they are',
          render: onDev('help-age'),
          note: (
            <Note title='The rule' colour='sunflower' variant='body2'>
              If they have already said how old they are, we don't ask.
            </Note>
          ),
        },
        {
          id: 'dev-phone',
          title: 'Your phone number',
          render: onDev('help-phone'),
        },
        {
          id: 'dev-start',
          title: 'Where to start',
          render: onDev('help-start-talk'),
        },
        {
          id: 'dev-book-call',
          title: 'Book a call page',
          render: onDev('book-call-placeholder'),
        },
      ],
    },
    {
      // Their first answer left out what they need help with: once the first
      // summary is confirmed they are asked, it is read, and the summary comes
      // back with it in to check, before where they live.
      title: "If they didn't say what they need help with",
      arrows: false,
      steps: [
        {
          id: 'need-first-summary',
          title: 'Does this look right?',
          render: onDev('help-need-help-first-summary'),
        },
        {
          id: 'need-ask',
          title: 'What they most want help with',
          render: onDev('help-need-help'),
        },
        {
          id: 'need-reading',
          title: 'Reading it',
          render: onDev('help-need-help-reading'),
        },
        {
          id: 'need-summary',
          title: 'Does this look right? With it in',
          render: onDev('help-need-help-summary'),
        },
        {
          id: 'need-location',
          title: 'Where they live, as normal',
          render: onDev('help-questions'),
        },
        {
          id: 'need-rest',
          title: 'The rest of the flow, as normal',
          render: restAsNormal('how old they are'),
        },
      ],
    },
    {
      // The other way in, under the Happy path's first screens: what we do and
      // the consent on screens of their own, as before, then the chat starting
      // fresh on the first question. 1B, 2B and 3B stand in for its 1 to 3.
      title: 'Option B, for A/B testing',
      arrows: false,
      steps: [
        {
          id: 'b-what-we-do',
          title: '1B · What we do',
          render: onDev('youre-in-b'),
        },
        {
          id: 'b-consent',
          title: '2B · How we use AI',
          render: onDev('ai-consent'),
        },
        {
          id: 'b-chat-fresh',
          title: '3B · The chat, starting fresh',
          render: onDev('help-ask-fresh'),
        },
        {
          id: 'b-rest',
          title: '4B · The rest of the flow, as normal',
          render: restAsNormal('recording'),
        },
      ],
    },
    {
      // Where to start's quieter row: out of the chat into the app instead.
      title: 'Do this later',
      arrows: false,
      steps: [
        {
          id: 'later-start',
          title: 'Where to start',
          render: onDev('help-start'),
        },
        {
          id: 'later-home',
          title: 'Home page',
          render: onDev('home-placeholder'),
        },
      ],
    },
    {
      title: 'Phone number left for later',
      arrows: false,
      steps: [
        {
          id: 'phone-later',
          title: 'Do this later, then where to start',
          render: onDev('help-phone-later'),
        },
      ],
    },
    {
      title: 'Under 18',
      arrows: false,
      steps: [
        {
          id: 'under-18',
          title: 'A kind stop, and where to go instead',
          render: onDev('help-under-18'),
        },
      ],
    },
    {
      // Lifted out of the happy path, which now goes from how old they are
      // straight to where to start. Kept for later, not deleted.
      title: 'The middle of the journey',
      journey: 'Parked',
      steps: [
        {
          id: 'dev-how-we-help',
          title: 'How we can help',
          render: onDev('help-how'),
        },
        {
          id: 'dev-what-we-need',
          title: "What we'd need",
          render: onDev('help-need'),
        },
        {
          id: 'dev-gather',
          title: 'Send us what you have',
          render: onDev('help-gather'),
        },
        {
          id: 'dev-gathered',
          title: 'Some of it sent',
          render: onDev('help-gathered'),
        },
        {
          id: 'dev-collected',
          title: "Here's what we've got",
          render: onDev('help-collected'),
        },
        {
          id: 'dev-collected-editing',
          title: 'Correcting it',
          render: onDev('help-collected-editing'),
        },
      ],
    },
    {
      title: 'Caring for yourself, the middle',
      journey: 'Parked',
      steps: [
        {
          id: 'self-how',
          title: 'How we can help',
          render: onDevSelfFull('help-how'),
        },
        {
          id: 'self-need',
          title: "What we'd need",
          render: onDevSelfFull('help-need'),
        },
      ],
    },
    {
      // Read the full AI policy, from How we use AI: out of the app to where
      // the policy is hosted.
      title: 'Reading the AI policy',
      steps: [
        {
          id: 'ai-policy-explain',
          title: 'How we use AI',
          render: onDev('consent-explain'),
        },
        {
          id: 'ai-policy-page',
          title: 'AI policy, on our website',
          render: onDev('ai-policy-placeholder'),
        },
      ],
    },
    {
      // I'd rather not, on the consent: the onboarding without the AI.
      title: 'No AI consent',
      arrows: false,
      steps: [
        {
          id: 'no-ai-consent',
          title: 'What we do, and the consent',
          render: onDev('chat-v2'),
        },
        {
          id: 'old-onboarding',
          title: "I'd rather not",
          render: onDev('old-onboarding'),
        },
      ],
    },
    {
      title: 'Not quite right',
      arrows: false,
      steps: [
        {
          id: 'not-quite-summary',
          title: 'Does this look right?',
          render: onDev('help-summary'),
        },
        {
          id: 'not-quite-ask',
          title: 'What needs changing',
          render: onDev('help-not-quite'),
        },
        {
          id: 'not-quite-fixed',
          title: 'The summary, changed',
          render: onDev('help-summary-fixed'),
        },
      ],
    },
    {
      // Beside Not quite right: the other ways to correct the summary, none
      // of them through the chip.
      title: 'Typing the fix',
      arrows: false,
      beside: true,
      steps: [
        {
          id: 'corrected-misheard',
          title: 'Does this look right? Fourteen, not forty',
          render: onDevCorrected('help-summary'),
        },
        {
          id: 'corrected-typing',
          title: 'Typing the correction',
          render: onDevCorrected('help-summary-typing'),
        },
        {
          id: 'corrected-reading',
          title: 'Reading it',
          render: onDevCorrected('help-summary-correcting'),
        },
        {
          id: 'corrected-summary',
          title: 'The summary, fixed',
          render: onDevCorrected('help-summary-corrected'),
        },
      ],
    },
    {
      title: 'With the pencil',
      arrows: false,
      beside: true,
      steps: [
        {
          id: 'pencil-misheard',
          title: 'Does this look right? Fourteen, not forty',
          render: onDevCorrected('help-summary'),
        },
        {
          id: 'pencil-open',
          title: 'The pencil tapped, its lines to change',
          render: onDevCorrected('help-summary-pencil'),
        },
        {
          id: 'pencil-fixed',
          title: 'The summary, fixed',
          render: onDevCorrected('help-summary-corrected'),
        },
      ],
    },
    {
      title: 'A new voice note',
      arrows: false,
      beside: true,
      steps: [
        {
          id: 'voice-misheard',
          title: 'Does this look right? Fourteen, not forty',
          render: onDevCorrected('help-summary'),
        },
        {
          id: 'voice-recording',
          title: 'Recording, without tapping Not quite',
          render: onDevCorrected('help-summary-recording'),
        },
        {
          id: 'voice-reading',
          title: 'Reading it',
          render: onDevCorrected('help-summary-voice-reading'),
        },
        {
          id: 'voice-fixed',
          title: 'The summary, fixed',
          render: onDevCorrected('help-summary-corrected'),
        },
      ],
    },
    {
      title: 'Not right now',
      journey: 'Parked',
      arrows: false,
      noteAfter: (
        <Note title='Open questions' variant='body2'>
          <Stack spacing={1.5}>
            <span>
              Which of these do we absolutely need before they can carry on? A
              phone number might be one, so our care experts can call them.
            </span>
            <span>
              If they don't give us any of it for now, what do we want them to
              do next? We need to point them somewhere.
            </span>
          </Stack>
        </Note>
      ),
      steps: [
        {
          id: 'not-now',
          title: "Not right now, then what we'd need",
          render: onDev('help-not-now'),
        },
        {
          id: 'later',
          title: "I'll do it later, then where to start",
          render: onDev('help-later'),
        },
      ],
    },
    {
      title: 'Answers that change the offer',
      journey: 'Parked',
      arrows: false,
      steps: [
        {
          id: 'how-scotland',
          title: 'In Scotland',
          render: onDev('help-how-scotland'),
        },
        {
          id: 'how-younger',
          title: 'Under pension age',
          render: onDev('help-how-younger'),
        },
        {
          id: 'how-no-daily-help',
          title: 'No help needed day to day',
          render: onDev('help-how-no-daily-help'),
        },
      ],
    },
    {
      // Beside the offer: answers given some other way than the one asked
      // for. Dev reads whatever they say, so a typed answer can cover more
      // than the question it was given to.
      title: 'Out of sequence',
      journey: 'Parked',
      arrows: false,
      beside: true,
      steps: [
        {
          id: 'own-words-typed',
          title: 'Where she lives, typed, with her age in it',
          render: onDev('help-own-words-typed'),
        },
        {
          id: 'own-words-answered',
          title: 'Thanks, and the age question skipped',
          render: onDev('help-own-words-answered'),
        },
      ],
    },
    {
      title: 'Changing an earlier answer',
      journey: 'Parked',
      arrows: false,
      beside: true,
      steps: [
        {
          id: 'change-answer',
          title: 'Where she lives, tapped to change it',
          render: onDev('help-change-answer'),
        },
        {
          id: 'change-answer-done',
          title: 'Changed, and said so',
          render: onDev('help-change-answer-done'),
        },
      ],
      noteAfter: (
        <Note title='Not in dev' colour='cherry' variant='body2'>
          <Stack spacing={1.5}>
            <span>
              Dev only lets them change the summary. Here they tap an earlier
              answer and the same rows open again in the chat. They pick
              another, and a line under it says it changed.
            </span>
            <span>
              Open question: can they change a triage answer once it is given,
              and if it changes the offer, does How we can help update?
            </span>
          </Stack>
        </Note>
      ),
    },
    {
      title: 'Caring for yourself',
      arrows: false,
      steps: [
        {
          id: 'self-recording',
          title: 'Recording, captions on',
          render: onDevSelf('help-recording-captions'),
        },
        {
          id: 'self-summary',
          title: 'Does this look right?',
          render: onDevSelf('help-summary'),
        },
        {
          id: 'self-questions',
          title: 'What the summary left',
          render: onDevSelf('help-questions'),
        },
        {
          id: 'self-age',
          title: 'How old you are',
          render: onDevSelf('help-age'),
        },
        {
          id: 'self-phone',
          title: 'Your phone number',
          render: onDevSelf('help-phone'),
        },
        {
          id: 'self-start',
          title: 'Where to start',
          render: onDevSelf('help-start'),
        },
      ],
    },
    {
      title: 'Other ways to answer',
      arrows: false,
      steps: [
        {
          id: 'typed',
          title: 'Typed instead',
          render: onDev('help-typed'),
        },
        {
          id: 'photo',
          title: 'A photo instead',
          render: onDev('help-photo'),
        },
        {
          id: 'resumed',
          title: 'Coming back',
          render: onDev('help-resumed'),
        },
      ],
    },
    {
      title: 'Something missing',
      arrows: false,
      steps: [
        {
          id: 'missing-recording',
          title: 'Recording, captions on',
          render: onDevMissing('help-recording-captions'),
        },
        {
          id: 'missing-processing',
          title: 'Reading what they said',
          render: onDevMissing('help-processing'),
        },
        {
          id: 'missing-summary',
          title: 'Does this look right?',
          render: onDevMissing('help-summary'),
        },
        {
          id: 'missing-saying-more',
          title: 'Saying more, the prompts back',
          render: onDevMissing('help-summary-recording'),
          note: (
            <Note title='The rule' colour='sunflower' variant='body2'>
              The turn ends when they say the summary is right, not when they
              send. If they start recording or typing before that and something
              on the card is still unticked, it pins itself again so they can
              see what is missing. If everything is ticked, it stays where it
              is.
            </Note>
          ),
        },
        {
          id: 'missing-summary-again',
          title: 'Read again, all of it',
          render: onDev('help-summary'),
        },
      ],
    },
  ],
  states: [
    {
      id: 'too-short',
      title: 'Too short to read',
      kind: 'edge',
      render: onDev('help-too-short'),
    },
    {
      id: 'not-understood',
      title: "Couldn't make it out",
      kind: 'edge',
      render: onDev('help-not-understood'),
    },
    {
      id: 'mic-blocked',
      title: 'Microphone blocked',
      kind: 'edge',
      render: onDev('help-mic-blocked'),
    },
    {
      id: 'fail-send-voice',
      title: "Voice note didn't send",
      kind: 'error',
      render: onDev('help-fail-send-voice'),
    },
    {
      id: 'fail-send',
      title: "Message didn't send",
      kind: 'error',
      render: onDev('help-fail-send'),
    },
    {
      id: 'phone-short',
      title: 'Phone number too short',
      kind: 'error',
      render: onDev('help-phone-short'),
    },
    {
      id: 'phone-long',
      title: 'Phone number too long',
      kind: 'error',
      render: onDev('help-phone-long'),
    },
    {
      id: 'fail-upload',
      title: "Photo didn't upload",
      kind: 'error',
      render: onDev('help-fail-upload'),
    },
  ],
  components: {
    mui: ['Card', 'Chip', 'Collapse', 'IconButton', 'InputBase'],
    existing: ['ChatBubble', 'VoiceNote', 'Attachment', 'FailedMessage'],
    new: [
      'FromUsCard',
      'ChatMenuMessage',
      'LiveCaptionCard',
      'ReadingBubble',
      'QuickReplies',
      'ComposerCard',
    ],
  },
};
